import * as Accordion from '@radix-ui/react-accordion';
import { useCallback } from 'react';

import { DropdownItem } from '~/chords/DropdownTreeSingleSelect/components/DropdownItem';
import { DropdownItems } from '~/chords/DropdownTreeSingleSelect/components/DropdownItems';
import type {
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/chords/DropdownTreeSingleSelect/types';
import { cn } from '~/chords/lib/cn';

interface DropdownItemWithChildrenProps extends RadixUiProps {
  depth: number;
  option: InternalDropdownOption;
  selectedOption?: InternalDropdownOption;
  onSelectOption?: (option: InternalDropdownOption) => void;
  onExpandOption?: (
    option: InternalDropdownOption,
    isExpanded: boolean,
  ) => void;
  showCheckIcon?: boolean;
  searchQuery?: string;
  OptionTemplate?: OptionTemplateComponent;
}

export const ACCORDION_TYPE = 'multiple';

export function DropdownItemWithChildren({
  depth,
  option,
  selectedOption,
  onSelectOption,
  onExpandOption,
  showCheckIcon,
  searchQuery,
  OptionTemplate,
  radix = {},
}: DropdownItemWithChildrenProps) {
  const handleAccordionValueChange = useCallback(
    (value: string[]) => {
      onExpandOption?.(option, !!value.length);
    },
    [option, onExpandOption],
  );

  if (!option.isVisible) {
    return null;
  }

  return (
    <Accordion.Root
      {...radix.accordion}
      type={ACCORDION_TYPE}
      onValueChange={handleAccordionValueChange}
      defaultValue={option.isExpanded ? [option.value] : undefined}
      asChild
    >
      <ul>
        <Accordion.Item {...radix.accordionItem} value={option.value} asChild>
          <li>
            <DropdownItem
              key={option.value}
              depth={depth}
              option={option}
              selectedOption={selectedOption}
              onSelectOption={onSelectOption}
              showCheckIcon={showCheckIcon}
              searchQuery={searchQuery}
              OptionTemplate={OptionTemplate}
              radix={radix}
            />
            {option.children?.length ? (
              <Accordion.Content
                {...radix.accordionContent}
                className={cn(
                  'overflow-hidden',
                  // Animate the expanding and collapsing of the nested options.
                  'data-[state=open]:animate-accordion-slide-down',
                  'data-[state=closed]:animate-accordion-slide-up',
                  radix.accordionContent?.className,
                )}
              >
                <DropdownItems
                  depth={depth + 1}
                  options={option.children}
                  searchQuery={searchQuery}
                  selectedOption={selectedOption}
                  onSelectOption={onSelectOption}
                  onExpandOption={onExpandOption}
                  showCheckIcon={showCheckIcon}
                  OptionTemplate={OptionTemplate}
                  radix={radix}
                />
              </Accordion.Content>
            ) : null}
          </li>
        </Accordion.Item>
      </ul>
    </Accordion.Root>
  );
}

DropdownItemWithChildren.displayName = 'DropdownItemWithChildren';
